import {
  AUDIO_FILE,
  CAMERA_PRESENTER,
  SCREEN_PRESENTER
} from '@shoflo/polymer';
import { getCameraSources } from '@studio/program';

import { FILL_FRAME, BackgroundStyles } from 'constants/LayerPositions';
import { CAMERA_PRESENTER_LIMIT } from 'constants/SceneLayouts';

export const getSceneLayerIds = broadcast => {
  const { globalSceneLayers } = broadcast;

  const sceneLayers = globalSceneLayers.length
    ? globalSceneLayers
    : ['SCENE_LAYER'];

  return sceneLayers.map(sl => {
    return sl._id ? sl._id : sl;
  });
};

export const orderSources = sources => {
  return [...sources].sort((a, b) => a.order - b.order);
};

export const getCurrentUserId = user => {
  return user ? user._id : null;
};

export const isDifferentDirector = (originator, receiver) =>
  // constrain to valid user ids
  typeof originator === 'string' &&
  Boolean(originator) &&
  originator !== receiver;

/**
 * Util for toggling the hidden status of an banners, backgrounds
 * with the following structure [{ _id, layer: {}, source: {}}]
 * we can only have one un-hidden at a time
 */
export const toggleStatus = (itemId, items) =>
  items.reduce((acc, item) => {
    const { source, _id } = item;
    const isItemHidden = !(_id === itemId && source.hidden);
    return [...acc, { ...item, source: { ...source, hidden: isItemHidden } }];
  }, []);

/**
 * Util for toggling the hidden status of an overlays
 * with the following structure [{ _id, layer: {}, source: {}}]
 * We can have multiple overlays un-hidden
 */
export const toggleOverlayStatus = (overlayId, overlays) => {
  return overlays.map(overlay => {
    const { source, _id } = overlay;
    return _id === overlayId
      ? { ...overlay, source: { ...source, hidden: !source.hidden } }
      : overlay;
  });
};

/**
 * Util for toggling theposition of backgrounds
 * with the following structure [{ _id, layer: {}, source: {}}]
 * we can only have one un-hidden at a time
 */
export const togglePosition = (itemId, items) =>
  items.reduce((acc, item) => {
    const { layer, _id } = item;
    let title = layer.title;
    if (_id === itemId) {
      title = FILL_FRAME;
    }
    return [
      ...acc,
      {
        ...item,
        layer: {
          ...layer,
          title,
          style: BackgroundStyles[title]
        }
      }
    ];
  }, []);

/**
 * Check if source is a screen presenter
 * @param {*} source
 * @returns Object
 */
export const getCameraPresenterScreenShare = (source = {}, { sources }) => {
  const [screenShareSource] = sources.filter(
    s => s.value === getScreenShareValue(source.value)
  );

  return screenShareSource;
};

export const getScreenShareValue = sourceValue =>
  sourceValue && `${sourceValue}#content`;

/**
 * Get layer braodcast sources
 * Need to make sure the layer sources exist in the broadcastSources array
 */
export const getBroadcastSourcesFromLayers = (layers, broadcastSources) => {
  return layers
    .map(layer => layer.source)
    .filter(source =>
      broadcastSources.find(bs => [source._id, source.value].includes(bs._id))
    );
};

/**
 * Check that the source is Audio source
 * @param {*} source
 * @returns
 */
export const isAudioSource = source => source && source.type === AUDIO_FILE;
export const isScreenSharePresenter = (source = {}) =>
  source.type === SCREEN_PRESENTER;

/**
 * Check that the source is Camera source
 */
export const isCameraPresenter = source =>
  source && source.type === CAMERA_PRESENTER;

export const getCreatedScene = (storeScenes, broadcastScenes) => {
  const scenesMap = storeScenes.reduce((acc, curr) => {
    acc[curr._id] = true;
    return acc;
  }, {});

  const [createdScene] = broadcastScenes.filter(scene => !scenesMap[scene._id]);
  return createdScene;
};

/**
 * Repopulate scene with the correct audio source
 * @param {{}} sourceScene
 * @param {{}} targetScene
 * @returns {{}}
 */
export const transferAudioSourceLayer = (sourceScene, targetScene) => {
  const sourceSceneAudioLayer = sourceScene?.layers?.find(layer =>
    isAudioSource(layer?.source)
  );

  if (!sourceSceneAudioLayer) return targetScene;

  let targetSceneAudioLayerIndex;
  const sceneAudioLayer = targetScene.layers.find((layer, index) => {
    targetSceneAudioLayerIndex = index;
    return isAudioSource(layer.source);
  });

  if (!sceneAudioLayer) {
    targetScene.layers.push(sourceSceneAudioLayer);
    return targetScene;
  }

  targetScene.layers[targetSceneAudioLayerIndex] = sourceSceneAudioLayer;
  return targetScene;
};

/**
 * Will adding a camera presenter exceed the camera presenter limit?
 *
 * @param sources
 * @param newSource
 */
export const willExceedCameraLimit = (sources, newSource) => {
  const cameraSources = getCameraSources(sources);

  return (
    newSource.type === CAMERA_PRESENTER &&
    cameraSources.length === CAMERA_PRESENTER_LIMIT
  );
};
